// Revival History — every scanned quote by the day it was scanned, as one table.
// The summary is a stat strip with real money ("$2,575,087.13", never a raw float);
// a row opens the quote in the movable window; the phone gets a list.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useIsPhone from "../../hooks/useIsPhone";
import { DataTable } from "../ui/table";
import Badge from "../ui/badge";
import Button from "../ui/button";
import RevivalHeader, { StatStrip } from "./RevivalHeader";
import QuoteWindow from "./QuoteWindow";
import { count, money, quoteTotal, shortDate, statusLabel, statusTone, useRevivalQuotes, useScanSummary } from "./revivalData";
import "./revival.css";

const scannedOn = (q) => String(q.created_at || "").slice(0, 10);

export default function RevivalHistory() {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { rows, loading, error, reload } = useRevivalQuotes();
  const { summary } = useScanSummary();
  const [open, setOpen] = useState(null);

  const scanned = useMemo(
    () => rows.filter((q) => q.source === "scanner").sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [rows]
  );

  const columns = [
    { key: "created_at", label: "Scanned", render: (q) => <span className="rv-cell-dim">{shortDate(scannedOn(q))}</span> },
    { key: "quote_number", label: "Quote", render: (q) => <span className="rv-cell-dim">{q.quote_number || "—"}</span> },
    { key: "customer_name", label: "Customer", sortValue: (q) => q.customer_name || null, render: (q) => <span className="rv-cell-name">{q.customer_name || "—"}</span> },
    { key: "status", label: "Status", render: (q) => <Badge tone={statusTone(q.status || "draft")}>{statusLabel(q.status || "draft")}</Badge> },
    { key: "amount_paid", label: "Paid", num: true, sortValue: (q) => Number(q.amount_paid ?? 0), render: (q) => money(q.amount_paid) },
    { key: "total", label: "Total", num: true, sortValue: (q) => quoteTotal(q), render: (q) => money(quoteTotal(q)) },
  ];

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="History"
        totals={summary ? [`${count(summary.total_scanned)} scanned`, money(summary.total_scanned_value)] : []}
        primary={<Button variant="primary" size="sm" onClick={() => navigate("/revival/scanner")}>Scan a ticket</Button>}
      />
      <StatStrip
        stats={[
          { label: "Scanned value", value: money(summary?.total_scanned_value) },
          { label: "Collected", value: money(summary?.total_collected) },
          { label: "Paid in full", value: count(summary?.paid_quotes) },
          { label: "Unpaid", value: count(summary?.unpaid_quotes) },
        ]}
      />
      {error ? <div className="rv-error" role="alert">{error}</div> : null}
      {loading ? <div className="rv-loading">Loading history…</div> : isPhone ? (
        <div className="rv-list">
          {scanned.map((q) => (
            <button key={q.id} type="button" className="rv-row" onClick={() => setOpen(q)}>
              <span className="rv-row-main">
                <b>{q.customer_name || "—"}</b>
                <span>{[shortDate(scannedOn(q)), q.quote_number].filter((s) => s && s !== "—").join(" · ")}</span>
              </span>
              <span className="rv-row-end">
                <b>{money(quoteTotal(q))}</b>
                <span>{statusLabel(q.status || "draft")}</span>
              </span>
            </button>
          ))}
          {!scanned.length ? <div className="rv-loading">No scans yet. Scan a ticket and it lands here.</div> : null}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={scanned}
          onRowClick={setOpen}
          maxHeight="calc(100vh - 220px)"
          empty="No scans yet. Scan a ticket and it lands here."
        />
      )}
      {open ? <QuoteWindow quote={open} onClose={() => setOpen(null)} onDeleted={reload} /> : null}
    </div>
  );
}
